// Product analytics (PostHog): how people move through the app, so we can see
// where they get stuck. Off unless the backend serves a project key.
//
// Privacy rules, on purpose:
// - No conversation content ever: every piece of text and every input is
//   masked in replays, and autocapture records no element text or attributes.
// - People are known by their user id only, never their email or name.
// - Nothing is stored on the device (memory persistence: no cookies, no
//   localStorage), so a new page load is a new visit until sign-in identifies it.
// - PostHog's code is bundled with the app and loads nothing from a third-party
//   script host, and only when a key is set, so it costs nothing otherwise.
import type { PostHog } from 'posthog-js/dist/module.full.no-external'

export type AnalyticsConfig = {
    posthogKey?: string | null
    posthogHost?: string | null
    environment?: string | null
}

export type AnalyticsUser = {
    id: string
    roles?: string[]
}

let client: PostHog | null = null
let starting: Promise<PostHog | null> | null = null
let environment: string | null = null

type Pending = [string, Record<string, string | number | boolean | null>?]
// Events tracked before PostHog has loaded. A screen shown as the page opens
// (the Captures start screen, on a direct visit or reload) tracks before the
// dynamic import lands, and was dropped, so the join funnel had no first step.
// Capped so it cannot grow where analytics is off and never starts.
const MAX_PENDING = 100
let pending: Pending[] = []

export function startAnalytics(
    config: AnalyticsConfig | undefined
): Promise<PostHog | null> {
    if (client) return Promise.resolve(client)
    if (starting) return starting
    const key = config?.posthogKey
    if (!key) return Promise.resolve(null)

    starting = import('posthog-js/dist/module.full.no-external')
        .then(({ default: posthog }) => {
            posthog.init(key, {
                // Sent through our own domain (nginx / Vite relay to PostHog
                // EU), so privacy blockers don't drop it.
                api_host: '/lf-r',
                ui_host: 'https://eu.posthog.com',
                persistence: 'memory',
                person_profiles: 'identified_only',
                capture_pageview: 'history_change',
                capture_pageleave: true,
                disable_external_dependency_loading: true,
                mask_all_text: true,
                mask_all_element_attributes: true,
                session_recording: {
                    maskAllInputs: true,
                    maskTextSelector: '*',
                    blockSelector: 'img, video, canvas',
                },
                // Unhandled browser errors go to PostHog error tracking, linked
                // to the (masked) replay. Messages and stack traces only.
                capture_exceptions: true,
                // Is the app easy to click around? Clicks on things that look
                // clickable but do nothing, and where people click and scroll.
                // Rage clicks are on by default.
                capture_dead_clicks: true,
                enable_heatmaps: true,
            })
            // Staging and prod share one project: tag every event.
            environment = config?.environment ?? null
            if (environment) posthog.register({ environment })
            client = posthog
            // After the caller's identify (which runs as this promise settles),
            // so the held events carry the user and workspace too.
            setTimeout(flushPending, 0)
            return posthog
        })
        .catch((err) => {
            // Analytics must never break the app.
            console.warn('Analytics did not start', err)
            starting = null
            return null
        })
    return starting
}

/** Ties this visit to a signed-in user, by id only, and their workspace. */
export function identifyUser(
    user: AnalyticsUser | null,
    tenantSlug: string | null | undefined
) {
    if (!client) return
    if (tenantSlug) client.register({ workspace: tenantSlug })
    if (user) client.identify(user.id, { roles: user.roles ?? [] })
}

/** A named step, e.g. track('capture_joined', { tables: 3 }). No content. */
export function track(
    event: string,
    properties?: Record<string, string | number | boolean | null>
) {
    if (client) {
        client.capture(event, properties)
    } else if (pending.length < MAX_PENDING) {
        pending.push([event, properties])
    }
}

function flushPending() {
    if (!client) return
    const held = pending
    pending = []
    for (const [event, properties] of held) client.capture(event, properties)
}

/** On sign-out, so the next person on a shared device is not linked to this one. */
export function resetAnalytics() {
    if (!client) return
    client.reset()
    // reset() also drops registered properties; the environment still applies.
    if (environment) client.register({ environment })
}

/** Tests only. */
export function _resetForTests() {
    client = null
    starting = null
    environment = null
    pending = []
}
